import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ChevronLeft, ChevronRight, Check, Trash2, CalendarClock, AlertTriangle, Info, CalendarCheck } from "lucide-react";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const monthLabel = (d) => d.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
const dayLabel = (key) => new Date(key).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });

/** Date-specific availability. Nothing recurs — a date is bookable only if it is saved here.
 *  Selections are staged across as many months as the cleaner wants and saved with ONE confirm;
 *  changing month never discards what they already picked. */
export default function AvailabilityCalendar({ compact = false, onChange, showCoverageError = false }) {
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});          // { "2026-07-04": { start, end } } across all months
  const [start, setStart] = useState("09:00");
  const [end, setEnd] = useState("17:00");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/availability/mine");
    setData(data);
    onChange?.(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const byDate = useMemo(() => Object.fromEntries((data?.days || []).map((d) => [d.date, d])), [data]);
  const draftKeys = useMemo(() => Object.keys(draft).sort(), [draft]);

  const cells = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const lead = (first.getDay() + 6) % 7;                       // Monday-first grid
    const total = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    return [...Array(lead).fill(null), ...Array.from({ length: total }, (_, i) =>
      new Date(cursor.getFullYear(), cursor.getMonth(), i + 1))];
  }, [cursor]);

  const todayIso = iso(new Date());
  const horizon = data?.max_horizon || "9999-12-31";

  const toggle = (d) => {
    const key = iso(d);
    if (key < todayIso || key > horizon) return;
    setDraft((s) => {
      if (s[key]) { const { [key]: _drop, ...rest } = s; return rest; }
      const existing = byDate[key]?.windows?.[0];
      return { ...s, [key]: { start: existing?.start || start, end: existing?.end || end } };
    });
  };

  const setHours = (key, field, value) =>
    setDraft((s) => ({ ...s, [key]: { ...s[key], [field]: value } }));

  const applyHoursToAll = () =>
    setDraft((s) => Object.fromEntries(Object.keys(s).map((k) => [k, { start, end }])));

  const confirm = async () => {
    if (draftKeys.length === 0) return toast.error("Tap the dates you're available first");
    const bad = draftKeys.find((k) => draft[k].start >= draft[k].end);
    if (bad) return toast.error(`${dayLabel(bad)}: the finish time must be after the start time`);
    setSaving(true);
    try {
      const { data: res } = await api.post("/availability/bulk", {
        dates: [], days: draftKeys.map((k) => ({ date: k, windows: [draft[k]] })),
      });
      toast.success(`Availability confirmed on ${res.saved.length} date(s)`);
      setDraft({});
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const removeOne = async (key) => {
    try { await api.delete(`/availability/${key}`); toast.success(`${dayLabel(key)} is now unavailable`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const selectWeekdaysInMonth = () => {
    const keys = cells.filter(Boolean).map(iso)
      .filter((k) => k >= todayIso && k <= horizon)
      .filter((k) => { const wd = new Date(k).getDay(); return wd >= 1 && wd <= 5; });
    setDraft((s) => ({ ...s, ...Object.fromEntries(keys.map((k) => [k, s[k] || { start, end }])) }));
  };

  if (!data) return <Loader2 className="h-6 w-6 animate-spin text-accent" />;
  const upcoming = (data.days || []).filter((d) => d.date >= todayIso).slice(0, 60);
  const met = data.coverage_met;

  return (
    <div className="space-y-5" data-testid="availability-calendar">
      {/* Coverage requirement: at least 2 months, up to 6 months ahead */}
      <div className={`rounded-2xl border p-4 ${showCoverageError && !met ? "border-red-300 bg-red-50"
        : met ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}
        data-testid="availability-coverage">
        <p className="font-semibold text-sm inline-flex items-center gap-2">
          {met ? <Check className="h-4 w-4 text-emerald-600" />
            : <AlertTriangle className={`h-4 w-4 ${showCoverageError ? "text-red-600" : "text-amber-600"}`} />}
          {met ? "All good ✓" : showCoverageError ? "You can't continue yet" : "Please add your availability."}
        </p>
        <p className="text-sm mt-1" data-testid="availability-coverage-note">
          {met ? data.coverage_met_message
            : showCoverageError ? data.coverage_error : data.coverage_prompt}
        </p>
        {data.covered_until && (
          <p className="text-xs text-muted-foreground mt-2" data-testid="availability-covered-until">
            {data.days_published} date(s) confirmed · your calendar currently runs to{" "}
            {new Date(data.covered_until).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
          </p>
        )}
      </div>

      <div className="bg-card rounded-2xl border border-primary/5 p-4 sm:p-5">
        <div className="flex items-center justify-between">
          <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
            className="p-2 rounded-full hover:bg-secondary" data-testid="cal-prev-month" aria-label="Previous month">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <p className="font-display font-semibold" data-testid="cal-month">{monthLabel(cursor)}</p>
          <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
            className="p-2 rounded-full hover:bg-secondary" data-testid="cal-next-month" aria-label="Next month">
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
        <p className="text-xs text-muted-foreground text-center mt-1">
          Move between months freely — your selections are kept until you confirm.
        </p>

        <div className="grid grid-cols-7 gap-1 mt-4 text-center text-xs text-muted-foreground">
          {WEEKDAYS.map((w) => <span key={w}>{w}</span>)}
        </div>
        <div className="grid grid-cols-7 gap-1 mt-1">
          {cells.map((d, i) => {
            if (!d) return <span key={`x${i}`} />;
            const key = iso(d);
            const blocked = key < todayIso || key > horizon;
            const saved = byDate[key];
            const isSel = !!draft[key];
            return (
              <button key={key} onClick={() => toggle(d)} disabled={blocked} aria-pressed={isSel}
                className={`aspect-square rounded-xl text-sm flex flex-col items-center justify-center transition-colors border-2
                  ${blocked ? "opacity-30 cursor-not-allowed border-transparent" : ""}
                  ${isSel ? "border-accent bg-accent text-white"
                    : saved ? "border-emerald-300 bg-emerald-50 text-emerald-900"
                      : "border-border hover:border-accent/40"}`}
                data-testid={`cal-day-${key}`}>
                <span className="font-medium">{d.getDate()}</span>
                {saved && !isSel && <span className="text-[9px] leading-tight">{saved.windows[0]?.start}</span>}
                {isSel && <Check className="h-3 w-3" />}
              </button>
            );
          })}
        </div>

        <div className="mt-4 flex flex-wrap gap-2 text-xs">
          <button onClick={selectWeekdaysInMonth} className="px-3 py-1.5 rounded-full border border-border hover:bg-secondary" data-testid="cal-select-weekdays">Select weekdays this month</button>
          {draftKeys.length > 0 && (
            <button onClick={() => setDraft({})} className="px-3 py-1.5 rounded-full border border-border hover:bg-secondary" data-testid="cal-clear-selection">Clear all selections ({draftKeys.length})</button>
          )}
        </div>
      </div>

      <div className="bg-card rounded-2xl border border-primary/5 p-4 sm:p-5 space-y-4" data-testid="availability-editor">
        <p className="text-sm font-semibold">
          {draftKeys.length > 0
            ? `${draftKeys.length} date(s) selected across ${new Set(draftKeys.map((k) => k.slice(0, 7))).size} month(s)`
            : "Tap dates above, then set your hours"}
        </p>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm">Available from
            <input type="time" value={start} onChange={(e) => setStart(e.target.value)} className="av-input" data-testid="avail-start" />
          </label>
          <label className="text-sm">Until
            <input type="time" value={end} onChange={(e) => setEnd(e.target.value)} className="av-input" data-testid="avail-end" />
          </label>
        </div>

        {draftKeys.length > 0 && (
          <div className="rounded-xl border border-border divide-y divide-border max-h-72 overflow-y-auto" data-testid="avail-draft-list">
            {draftKeys.map((k) => (
              <div key={k} className="flex items-center gap-2 px-3 py-2 text-sm" data-testid={`avail-draft-${k}`}>
                <span className="font-medium w-20 sm:w-28 shrink-0 truncate">{dayLabel(k)}</span>
                <input type="time" value={draft[k].start} onChange={(e) => setHours(k, "start", e.target.value)} className="av-inline min-w-0 flex-1" data-testid={`avail-draft-start-${k}`} />
                <span className="text-muted-foreground">–</span>
                <input type="time" value={draft[k].end} onChange={(e) => setHours(k, "end", e.target.value)} className="av-inline min-w-0 flex-1" data-testid={`avail-draft-end-${k}`} />
                <button onClick={() => toggle(new Date(k))} className="p-1.5 rounded-full hover:bg-secondary shrink-0" aria-label={`Remove ${k}`} data-testid={`avail-draft-remove-${k}`}>
                  <Trash2 className="h-4 w-4 text-destructive" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <button onClick={confirm} disabled={saving || draftKeys.length === 0}
            className="px-6 py-3 rounded-full bg-accent text-white font-medium inline-flex items-center gap-2 disabled:opacity-50" data-testid="avail-apply">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarCheck className="h-4 w-4" />}
            Confirm availability{draftKeys.length > 0 ? ` (${draftKeys.length})` : ""}
          </button>
          {draftKeys.length > 1 && (
            <button onClick={applyHoursToAll} className="px-5 py-3 rounded-full border border-border font-medium" data-testid="avail-apply-hours-all">
              Use {start}–{end} for all selected
            </button>
          )}
        </div>
        <p className="text-xs text-muted-foreground inline-flex items-start gap-1.5" data-testid="avail-warning">
          <Info className="h-4 w-4 shrink-0" /> {data.warning} You can plan up to 6 months ahead.
        </p>
      </div>

      <div className="bg-card rounded-2xl border border-primary/5 p-4 sm:p-5" data-testid="availability-list">
        <p className="text-sm font-semibold inline-flex items-center gap-2"><CalendarClock className="h-4 w-4 text-accent" /> Your confirmed dates</p>
        {upcoming.length === 0 ? (
          <p className="text-sm text-muted-foreground mt-2">No dates confirmed yet — customers cannot book you until you confirm some dates.</p>
        ) : (
          <div className="mt-3 divide-y divide-border max-h-80 overflow-y-auto">
            {upcoming.map((d) => (
              <div key={d.date} className="flex items-center gap-3 py-2.5 text-sm" data-testid={`avail-row-${d.date}`}>
                <span className="font-medium">{dayLabel(d.date)}</span>
                <span className="text-muted-foreground">{d.windows.map((w) => `${w.start}–${w.end}`).join(", ")}</span>
                <button onClick={() => setDraft((s) => ({ ...s, [d.date]: { start: d.windows[0]?.start || start, end: d.windows[0]?.end || end } }))}
                  className="ml-auto text-accent font-medium" data-testid={`avail-edit-${d.date}`}>Edit</button>
                <button onClick={() => removeOne(d.date)} className="p-1.5 rounded-full hover:bg-secondary" aria-label={`Remove availability on ${d.date}`} data-testid={`avail-delete-${d.date}`}>
                  <Trash2 className="h-4 w-4 text-destructive" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      <style>{`.av-input{width:100%;margin-top:0.25rem;padding:0.625rem 0.75rem;border-radius:0.75rem;border:1px solid hsl(var(--border));background:hsl(var(--background));outline:none}.av-input:focus{border-color:hsl(var(--accent))}.av-inline{padding:0.3rem 0.5rem;border-radius:0.5rem;border:1px solid hsl(var(--border));background:hsl(var(--background));outline:none;font-size:0.8rem}.av-inline:focus{border-color:hsl(var(--accent))}`}</style>
    </div>
  );
}
